import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Minus, PackagePlus, Plus, ScanLine } from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Breadcrumb from "src/components/ui/Breadcrumb";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import FilterSelect from "src/components/ui/FilterSelect";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";
import StockSubNav from "src/components/inventory/StockSubNav";
import ScanProductModal from "src/components/modals/ScanProductModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as stockActions from "src/redux/slices/stockSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import productApis from "src/api/product";
import stockApis from "src/api/stock";
import { Product } from "src/types/product.types";
import { formatVariantLabel } from "src/utils/common";
import { toast } from "react-toastify";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";

interface PrefillState {
  warehouseId?: string;
  productId?: string;
  variantId?: string;
}

const StockAdjustmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as PrefillState) || {};
  const dispatch = useAppDispatch();

  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const { adjustStock } = useAppSelector((state) => state.stock);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canAdjust =
    isTenantAdmin || (permissions?.stockAdjustment || []).includes("create");
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel } = useWarehouseLabel();

  const [warehouseId, setWarehouseId] = useState(
    prefill.warehouseId || assignedWarehouseId || ""
  );

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);
  const [product, setProduct] = useState<Product | null>(null);
  const [productLabel, setProductLabel] = useState("");
  const [variantId, setVariantId] = useState(prefill.variantId || "");
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const [liveStock, setLiveStock] = useState<{
    quantity: number;
    reserved: number;
    available: number;
  } | null>(null);
  const [liveStockLoading, setLiveStockLoading] = useState(false);

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill the product from a "Update" click on the Stock Levels page.
  useEffect(() => {
    if (prefill.productId) {
      productApis.getProduct(prefill.productId).then((res: any) => {
        const p: Product | undefined = res?.data;
        if (p) {
          setProduct(p);
          setProductLabel(`${p.name} (${p.sku || p.productId})`);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (adjustStock.data && submitted) {
      toast.success("Stock adjusted successfully");
      dispatch(stockActions.resetAdjustStock());
      setSubmitted(false);
      navigate("/app/stock/levels");
    } else if (adjustStock.error && submitted) {
      toast.error(`Error: ${adjustStock.error}`);
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustStock, submitted]);

  const isVariantProduct = product?.productType === "variant";
  const variantOptions = (product?.variants || []).map((v) => ({
    label: formatVariantLabel(v.optionValues) || v.sku || "Variant",
    value: v._id || "",
  }));

  const handleScannedProduct = (scannedProduct: Product, scannedVariantId: string) => {
    setProduct(scannedProduct);
    setProductLabel(`${scannedProduct.name} (${scannedProduct.sku || scannedProduct.productId})`);
    setVariantId(scannedVariantId || "");
  };

  const canFetchLiveStock = !!(
    warehouseId &&
    product?._id &&
    (!isVariantProduct || variantId)
  );

  useEffect(() => {
    if (!canFetchLiveStock) {
      setLiveStock(null);
      return;
    }
    let cancelled = false;
    setLiveStockLoading(true);
    stockApis
      .getStockLevels({
        warehouseId,
        productId: product!._id!,
        ...(isVariantProduct ? { variantId } : {}),
        limit: 1,
      })
      .then((res: any) => {
        if (cancelled) return;
        const row = res?.data?.[0];
        setLiveStock({
          quantity: row?.quantity || 0,
          reserved: row?.reserved || 0,
          available: row?.available ?? (row?.quantity || 0) - (row?.reserved || 0),
        });
      })
      .finally(() => {
        if (!cancelled) setLiveStockLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, product?._id, variantId, isVariantProduct, canFetchLiveStock]);

  const qtyNum = Number(quantity) || 0;
  const exceedsAvailable =
    direction === "out" && !!liveStock && qtyNum > liveStock.available;

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  const isValid =
    !!warehouseId &&
    !!product?._id &&
    (!isVariantProduct || !!variantId) &&
    qtyNum > 0 &&
    !exceedsAvailable;

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid || !product?._id || !canAdjust) return;
    setSubmitted(true);
    dispatch(
      stockActions.adjustStockRequest({
        warehouseId,
        productId: product._id,
        ...(isVariantProduct ? { variantId } : {}),
        direction,
        quantity: qtyNum,
        reason: reason.trim() || undefined,
      })
    );
  };

  return (
    <DashboardLayout
      meta={{
        title: "Adjust Stock | Invyntra",
        description: `Update on-hand quantity for one variant at a ${warehouseLabel.toLowerCase()}.`,
      }}
    >
      <div className="space-y-4">
        <Breadcrumb
          data={[
            { label: "Stock movements", path: "/app/stock/movements" },
            { label: "Adjust stock" },
          ]}
        />

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <PackagePlus size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Adjust stock
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Update on-hand quantity for one variant at a {warehouseLabel.toLowerCase()}.
              </p>
            </div>
          </div>
          <StockSubNav />
        </div>

        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <FilterSelect
                variant="boxed"
                label={warehouseLabel}
                required
                value={warehouseId}
                onChange={setWarehouseId}
                allLabel="Select"
                options={warehouseOptions}
                error={touched && !warehouseId}
                disabled={isWarehouseLocked}
                helperText={
                  isWarehouseLocked
                    ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                    : undefined
                }
              />
            </Card>

            <Card>
              <CardHeader
                title="Product"
                description="Search or scan to pick the product (and variant) to adjust."
                actions={
                  <Button
                    type="button"
                    variant="outline"
                    aria-label="Scan barcode"
                    onClick={() => setScanOpen(true)}
                  >
                    <ScanLine size={16} />
                  </Button>
                }
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <SearchInput<Product>
                  label="Product"
                  required
                  placeholder="Search by name, SKU, HSN, or product ID..."
                  value={productLabel}
                  onSearch={async (query) => {
                    const res = await productApis.getProductList({
                      page: 1,
                      limit: 10,
                      status: "active",
                      search: query,
                    });
                    const list: Product[] = res?.data?.data || res?.data || [];
                    return list.map((p) => ({
                      value: p._id || "",
                      label: `${p.name} (${p.sku || p.productId})`,
                      data: p,
                    }));
                  }}
                  onSelect={(option: SearchInputOption<Product>) => {
                    setProduct(option.data);
                    setProductLabel(option.label);
                    setVariantId("");
                  }}
                  onClear={() => {
                    setProduct(null);
                    setProductLabel("");
                    setVariantId("");
                  }}
                  renderOption={(option) => (
                    <div>
                      <div className="font-medium text-gray-800 dark:text-gray-100">{option.data.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        SKU: {option.data.sku || "-"} · HSN:{" "}
                        {option.data.hsnCode || "-"} ·{" "}
                        {option.data.productType === "variant"
                          ? "Has variants"
                          : "Single product"}
                      </div>
                    </div>
                  )}
                />

                {isVariantProduct && (
                  <FilterSelect
                    variant="boxed"
                    label="Variant"
                    required
                    value={variantId}
                    onChange={setVariantId}
                    allLabel="Select a variant"
                    options={variantOptions}
                    error={touched && !variantId}
                  />
                )}
              </div>
            </Card>

            <Card className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Direction
                </label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={direction === "in" ? "primary" : "outline"}
                    rounded="full"
                    onClick={() => setDirection("in")}
                  >
                    <Plus size={15} className="mr-1.5" /> Add
                  </Button>
                  <Button
                    type="button"
                    variant={direction === "out" ? "primary" : "outline"}
                    color="danger"
                    rounded="full"
                    onClick={() => setDirection("out")}
                  >
                    <Minus size={15} className="mr-1.5" /> Remove
                  </Button>
                </div>
              </div>

              <Input
                label={`Qty (${direction === "in" ? "add" : "remove"})`}
                required
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                error={(touched && qtyNum <= 0) || exceedsAvailable}
                helperText={
                  exceedsAvailable
                    ? `Only ${liveStock?.available ?? 0} available to remove`
                    : touched && qtyNum <= 0
                    ? "Enter a quantity greater than 0"
                    : undefined
                }
              />

              <Textarea
                label="Reason"
                optional
                placeholder="Optional note"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={adjustStock.loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  isLoading={adjustStock.loading}
                  disabled={adjustStock.loading || !canAdjust}
                  title={
                    !canAdjust
                      ? "You don't have permission to record stock adjustments"
                      : undefined
                  }
                >
                  Record adjustment
                </Button>
              </div>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Live stock
              </h3>
              {!canFetchLiveStock ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Select a {warehouseLabel.toLowerCase()} and product to see live stock.
                </p>
              ) : liveStockLoading ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Loading...</p>
              ) : (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 dark:text-gray-400">On hand</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {liveStock?.quantity ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Reserved</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {liveStock?.reserved ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-100 pt-2 dark:border-gray-700">
                    <span className="text-gray-500 dark:text-gray-400">Available</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {liveStock?.available ?? 0}
                    </span>
                  </div>
                  {qtyNum > 0 && (
                    <div className="mt-2 flex items-center justify-between rounded-lg bg-primary-50 px-3 py-2 dark:bg-primary-900/30">
                      <span className="text-primary-700 dark:text-primary-300">
                        After (on hand)
                      </span>
                      <span
                        className={`font-semibold ${
                          exceedsAvailable
                            ? "text-red-600 dark:text-red-400"
                            : "text-primary-700 dark:text-primary-300"
                        }`}
                      >
                        {direction === "in" ? "+" : "-"}
                        {qtyNum}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {product?._id && warehouseId && (
              <Card>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">
                    {warehouseOptions.find((w) => w.value === warehouseId)?.label}
                  </span>
                  {"  "}
                  {product.name}
                </p>
              </Card>
            )}

            <Card className="border-blue-100 bg-blue-50 dark:border-blue-900/40 dark:bg-blue-900/20">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Removals cannot exceed available (reserved is protected).
              </p>
            </Card>
          </div>
        </div>
      </div>

      <ScanProductModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onProductFound={handleScannedProduct}
      />
    </DashboardLayout>
  );
};

export default StockAdjustmentPage;
